import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// import MainLayout from "../../Layout/mainLayout";
import { Table } from "antd";
import ListTopSearch from "../../Global Functions/listTopSearch";
import { listProjectResale } from "../../Redux/Actions/projectResaleActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { columnsProjectResale } from "../../Global Functions/columnsAntd";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import * as XLSX from "xlsx";

const ListProjectResale = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [enumsProjects, setEnumsProjects] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const { projectResaleList, loading } = useSelector((state) => state.projectResale || {});
    const { enumsData } = useSelector((state) => state.enumsData || {});

    const fetchDetails = useCallback(async (page, limit, status, showLoader = true) => {
        const projectData = {
            page,
            limit,
            status,
            is_deleted: false,
            business_type: "resale",
            filter: debouncedSearch,
            ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
            ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
        };
        try {
            await dispatch(listProjectResale(projectData, showLoader));
        } catch {
            toast.error("Failed to fetch projects");
        }
    },
        [dispatch, debouncedSearch, dateRange]
    );

    // debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    // fetch on pagination/search/date change
    useEffect(() => {
        if (enumsProjects.length > 0) {
            fetchDetails(pagination.current, pagination.pageSize, enumsProjects, false);
        }
    }, [pagination.current, pagination.pageSize, debouncedSearch, dateRange, enumsProjects, fetchDetails]);

    // get enums
    useEffect(() => {
        dispatch(getEnums());
    }, [dispatch]);

    // set status enums
    useEffect(() => {
        if (enumsData?.data?.ProjectStatus) {
            const statusEnums = Object.values(enumsData.data.ProjectStatus).filter(
                (s) => s !== "declined"
            );
            if (statusEnums.length > 0) {
                setEnumsProjects(statusEnums);
            }
        }
    }, [enumsData]);

    // sync total
    useEffect(() => {
        if (projectResaleList?.total) {
            setPagination((prev) => ({ ...prev, total: projectResaleList.total }));
        }
    }, [projectResaleList]);

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const handleDateRangeApply = (start, end) => {
        setDateRange(start === null && end === null ? { start: null, end: null } : { start, end });
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleDownloadExcel = async () => {
        try {
            const projectData = {
                page: pagination.current,
                limit: pagination.pageSize,
                status: enumsProjects,
                is_deleted: false,
                filter: searchQuery,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };

            const response = await dispatch(listProjectResale(projectData, false));
            const rawData = response?.data || projectResaleList?.data || [];

            if (!Array.isArray(rawData) || rawData.length === 0) {
                toast.error("No data available to download");
                return;
            }

            const preparedData = rawData.map((project) => ({
                project_id: project.project_id,
                project_name: project.project_name,
                project_type: project.project_type?.name || "",
                builder: project.builder?.name || "",
                city: project.city?.city_name || "",
                state: project.state?.state_name || "",
                country: project.country?.country_name || "",
                construction_status: project.construction_status || "",
                average_price: project.average_price || "",
                starting_price: project.starting_price || "",
                status: project.status || "",
                created_at: project.created_at || "",
            }));

            const worksheet = XLSX.utils.json_to_sheet(preparedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "ProjectResale");
            XLSX.writeFile(workbook, "project_resale_list.xlsx");
            toast.success("Download started!");
        } catch (error) {
            toast.error("Failed to download data");
        }
    };
    
    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch]);

    return (
        <>
            <div className="bookings_table">
                <div className="table_component_all">
                    <div style={{ gridColumn: "span 5", height: "auto" }}>
                        <ListTopSearch
                            h3heading="Project Resale List"
                            setState={setSearchQuery}
                            buttonhead={canAdd ? "Add Resale Project" : null}
                            navigateTo={canAdd ? "/resale_project/add" : null}
                            onCalendarClick={() => setIsDatePopupVisible(true)}
                            onDownloadProject={canAdd && canEdit ? handleDownloadExcel : undefined}
                        />
                    </div>

                    <div className="custom_table">
                        <div style={{ width: "90%" }}>
                            <Table
                                dataSource={(Array.isArray(projectResaleList?.data) && projectResaleList.data) || []}
                                virtual
                                scroll={{ x: 1700, y: 500 }}
                                pagination={pagination}
                                onChange={handleTableChange}
                                loading={loading}
                                columns={columnsProjectResale(() => fetchDetails(pagination.current, pagination.pageSize, enumsProjects, false))}
                                rowClassName={() => "text-center"}
                                rowKey="_id"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListProjectResale;